/**
 * Shown instead of the board when there is no session (or the API returned 401).
 *
 * No auto-redirect to the IdP on mount: an expired token would then bounce the
 * user through Entra with no explanation, and a redirect loop from a
 * misconfigured app registration becomes an infinite one with no way to read
 * the error — which is exactly what a redirect-URI mismatch produces.
 *
 * This is one of the two places in the SPA that spells the acronym out — the other
 * is the head of the Wiki tab. Here because it is the first screen a new reader
 * sees and the only one they see before knowing what the thing is called; the header
 * carries `ASTRA · Target Discovery` alone, because an expansion on every page load
 * is a footer, not a name.
 *
 * `target-discovery`, lower case and hyphenated, is an **identifier** and is not
 * renamed: it is the Entra app registration, and a reader who retypes it as "ASTRA"
 * when asking an administrator for consent will be told no such application exists.
 */
import { LogIn } from 'lucide-react'
import { Button } from './ui/Button'
import { Card, CardBody } from './ui/Card'
import { Logo } from './Logo'

/**
 * The lockup, the name, the expansion. One definition rather than one per branch,
 * so the expansion has a single home in this file as well as in the SPA.
 */
function Masthead() {
  return (
    <div className="flex flex-col items-center gap-4">
      <Logo size={30} className="text-slate-100" />
      <div>
        <h1 className="font-display text-xl font-bold tracking-tight text-slate-100">
          ASTRA
          <span className="text-base font-medium text-slate-400">
            {' · '}Target Discovery
          </span>
        </h1>
        <p className="mt-1 font-display text-2xs font-semibold uppercase tracking-eyebrow text-slate-500">
          Advanced System for Target Research Agent
        </p>
      </div>
    </div>
  )
}

export function SignInGate({
  onSignIn,
  error,
  consentRequired = false,
}: {
  onSignIn: () => void
  error?: string | null
  /**
   * Sign-in worked; the tenant has not consented to this app's API scope. A
   * distinct state because the remedy is a directory admin, not another
   * sign-in — offering the button here sends the user round Entra forever.
   */
  consentRequired?: boolean
}) {
  return (
    <div className="flex h-full items-center justify-center p-8">
      <Card className="w-full max-w-md animate-rise">
        <CardBody className="flex flex-col items-center gap-6 px-6 py-8 text-center">
          <Masthead />

          {consentRequired ? (
            // No sign-in button in this branch, deliberately. The account is fine;
            // pressing it again just walks the same user through Entra to the same
            // refusal.
            <>
              <p className="text-sm text-slate-400">
                You are signed in, but this app is awaiting admin approval.
              </p>
              <p className="max-w-md text-xs leading-relaxed text-slate-500">
                A Corteva directory administrator needs to grant admin consent for the
                <code className="mx-1 rounded bg-slate-800 px-1 py-0.5 font-mono">
                  user
                </code>
                scope on the <strong>target-discovery</strong> app registration.
                Signing in again will not change this.
              </p>
            </>
          ) : (
            <>
              <p className="text-sm text-slate-400">
                Ranked crop-protection target proteins. Sign in to view the board.
              </p>
              <Button onClick={onSignIn}>
                <LogIn className="h-4 w-4" />
                Sign in with Corteva
              </Button>
              {error && (
                <p className="max-w-md text-xs leading-relaxed text-red-400">
                  {error}
                </p>
              )}
            </>
          )}
        </CardBody>
      </Card>
    </div>
  )
}
